// Layer 4 (UI) — the Maintenance status band (polish.P21, comp A `.band`): one
// card, four cells split by hairlines, each with its 3px status rule, caps
// label, primary figure and the comparison under it — the Meeting and Downtime
// band anatomy. Every figure is a field of `maintenanceBand()`; nothing here
// derives.

import { GlassCard } from '@/design-system';
import { cn } from '@/shared/cn';
import { vizToneColor } from '@/versions/v1/modules/_viz/viz-tone';
import type { BandCell, BandSegment } from './MaintenanceBand.transform';

function Segments({ segments }: { segments: readonly BandSegment[] }) {
  return (
    <>
      {segments.map((segment, index) =>
        segment.strong ? (
          <span key={index} className="font-semibold text-ink-secondary">{segment.text}</span>
        ) : (
          <span key={index}>{segment.text}</span>
        ),
      )}
    </>
  );
}

/** The compliance meter: the fill in the cell's tone, a tick at each step of
 *  the tone decode with its value under it. */
function Meter({ meter }: { meter: NonNullable<BandCell['meter']> }) {
  return (
    <div className="mt-1" data-band-meter>
      <div className="relative h-[5px] rounded-[3px] bg-[var(--hairline-strong)]">
        <i
          className="absolute inset-y-0 left-0 block rounded-[3px]"
          style={{ width: `${Math.min(100, Math.max(0, meter.pct))}%`, backgroundColor: vizToneColor(meter.tone) }}
        />
        {meter.ticks.map((tick) => (
          <u
            key={tick}
            aria-hidden
            data-band-meter-tick={tick}
            className="absolute -bottom-[3px] -top-[3px] w-[1.5px] bg-[var(--ink-tertiary)]"
            style={{ left: `${tick}%` }}
          />
        ))}
      </div>
      <div className="relative mt-0.5 h-3 text-[10px] tabular-nums text-muted" aria-hidden>
        {meter.ticks.map((tick) => (
          <span key={tick} className="absolute -translate-x-1/2" style={{ left: `${tick}%` }}>
            {tick}
          </span>
        ))}
      </div>
    </div>
  );
}

export function MaintenanceBand({ cells }: { cells: readonly BandCell[] }) {
  return (
    <GlassCard
      as="section"
      tone="matte"
      aria-label="Maintenance status"
      data-maintenance-band
      className="overflow-hidden rounded-2xl border-[var(--hairline-strong)] p-0"
    >
      <div className="grid grid-cols-2 xl:grid-cols-4">
        {cells.map((cell) => (
          <div
            key={cell.id}
            data-band-cell={cell.id}
            className="relative flex min-h-[96px] min-w-0 flex-col gap-1 border-b border-r border-[var(--hairline)] px-4 pb-2.5 pt-3 last:border-r-0 xl:border-b-0 [&:nth-child(2)]:border-r-0 xl:[&:nth-child(2)]:border-r"
          >
            <span aria-hidden className="absolute inset-x-0 top-0 h-[3px]" style={{ backgroundColor: vizToneColor(cell.tone) }} />
            <span className="truncate text-xs font-semibold uppercase tracking-[0.07em] text-muted">{cell.label}</span>
            <span className="text-2xl font-semibold leading-none tracking-[-0.01em] tabular-nums text-ink-primary" data-band-figure>
              {cell.figure}
              {cell.unit ? <span className="ml-1 text-xs font-medium tracking-normal text-muted">{cell.unit}</span> : null}
            </span>
            <span className={cn('text-xs leading-snug tabular-nums text-muted')} data-band-comparison>
              <Segments segments={cell.comparison} />
            </span>
            {cell.meter ? <Meter meter={cell.meter} /> : null}
          </div>
        ))}
      </div>
    </GlassCard>
  );
}
